import { Check, X, ScrollText } from "lucide-react";

const MCM_FEATURES = [
  "Selezione di comparabili reali da rogiti, OMI, banche dati notarili",
  "Coefficienti di omogeneizzazione tracciabili e documentati",
  "Eliminazione statistica degli outlier (z-score, IQR)",
  "Validazione incrociata con offerte di mercato attive",
  "Output: range di valore con intervallo di confidenza dichiarato",
  "Replicabilità del risultato da soggetto terzo qualificato",
];

const STANDARD_ISSUES = [
  "Stime “a sensazione” o per analogia generica",
  "Comparabili scelti soggettivamente, senza criterio statistico",
  "Coefficienti correttivi non documentati o assenti",
  "Nessun controllo sugli outlier né intervallo di confidenza",
  "Valore puntuale non difendibile in sede peritale o bancaria",
  "Risultato non riproducibile su scala industriale",
];

export default function MCMSection() {
  return (
    <section id="mcm" className="relative py-24 lg:py-32 bg-white border-t border-slate-100" data-testid="mcm-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-y-10 gap-x-2 md:gap-10 lg:gap-14">
          <div className="col-span-12 lg:col-span-4">
            <div className="enea-eyebrow flex items-center gap-3 mb-5">
              <ScrollText className="w-4 h-4 text-[#2c7da0]" />
              <span>02 · Metodo</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-[44px] font-display font-semibold leading-tight text-[#0b2545]">
              Market Comparison Method.
              <span className="block mt-3 text-slate-600 font-normal text-2xl lg:text-3xl">
                Non una stima. Un protocollo.
              </span>
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Il MCM è lo standard internazionale (IVS / EVS) per la valutazione immobiliare basata
              sulla comparazione di dati osservabili. Lo applichiamo con disciplina estimativa:
              ogni numero finale è derivato, non assunto.
            </p>
            <blockquote className="mt-8 border-l-2 border-[#2c7da0] pl-5 py-2 italic text-slate-700 leading-relaxed">
              «Una stima senza metodo è un'opinione.
              Una stima MCM è un risultato.»
            </blockquote>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* MCM column */}
              <div className="rounded-xl bg-white border border-slate-200 p-7 lg:p-9 shadow-sm hover:shadow-md transition-shadow" data-testid="mcm-column">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="enea-eyebrow">Protocollo</div>
                    <div className="mt-1 text-xl font-display font-semibold tracking-tight text-[#0b2545]">HEC AI · MCM</div>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-[#0d9488]/10 flex items-center justify-center">
                    <Check className="w-5 h-5 text-[#0d9488]" />
                  </div>
                </div>
                <ul className="space-y-4">
                  {MCM_FEATURES.map((t, i) => (
                    <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#0b2545] text-white text-[11px] font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Standard column */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-7 lg:p-9" data-testid="standard-column">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="enea-eyebrow" style={{ color: "#94a3b8" }}>Metodo</div>
                    <div className="mt-1 text-xl font-display font-semibold tracking-tight text-slate-500">Stima Standard</div>
                  </div>
                  <div className="w-11 h-11 rounded-full bg-slate-200 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
                <ul className="space-y-4">
                  {STANDARD_ISSUES.map((t, i) => (
                    <li key={i} className="flex gap-3 text-[15px] text-slate-500 leading-relaxed">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full border border-slate-300 text-slate-400 text-[11px] flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {[
                { k: "±0,8%", l: "Scarto medio stima vs rogito" },
                { k: "1.482", l: "Comparabili attivi nel modello" },
                { k: "IVS / EVS", l: "Standard di riferimento" },
              ].map((m) => (
                <div key={m.l} className="rounded-xl bg-white border border-slate-200 p-5 lg:p-6 shadow-sm">
                  <div className="text-2xl lg:text-3xl font-display font-semibold tracking-tight text-[#2c7da0]">{m.k}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">{m.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
